import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  IconButton,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MapContainer from '@shared/components/MapContainer';
import TDHlogo from '@shared/assets/customMarkerTDH.png';

import { getSubsidiariesNew } from '@shared/services/services';
import DialogMapModal from '@shared/components/Dialogs/Map';
import { orderByName } from '@shared/helpers';
import { useSelector } from 'react-redux';

import SubsidiariesList from './SubsidiariesList';
import SubsidiaryCard from './SubsidiaryCard';

const parseLatLng = (lat, lng) => {
  const latNum = Number(lat);
  const lngNum = Number(lng);

  if (!isFinite(latNum) || !isFinite(lngNum)) {
    console.error('Lat o Lng inválidos:', lat, lng);
    return null;
  }
  return { lat: latNum, lng: lngNum };
};

const SellPoints = () => {
  const [sucLatLong, setSucLatLong] = useState({
    lat: -31.407944,
    lng: -64.190279,
  });
  const [showModalDialog, setShowModalDialog] = useState(false);
  const [showModalDialogTitle, setShowModalDialogTitle] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const [textFilter, setTextFilter] = useState('');
  const [selectedSuc, setSelectedSuc] = useState(null);
  const [scrollToKey, setScrollToKey] = useState(null);
  const [markerModalSucursal, setMarkerModalSucursal] = useState(null);
  const [expandedMap, setExpandedMap] = useState(false);

  const { name } = useSelector(store => store.pageConfiguration);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetchSubsidiaries = async () => {
      let result = await getSubsidiariesNew();
      result = result.data.filter(el => el.status.name !== 'inactive');
      result = orderByName(result);
      setSucursales(result);
    };
    fetchSubsidiaries();
  }, []);

  const handleTextChange = e => {
    setTextFilter(e.target.value);
  };

  // Cambiar sucursal (selección desde la lista)
  const changeSuc = useCallback(
    sucursal => {
      const newSuc = parseLatLng(sucursal.latitude, sucursal.longitude);
      if (!newSuc) {
        return;
      }

      setSelectedSuc({
        key: sucursal.id ?? sucursal.name,
        name: sucursal.name,
        latLng: newSuc,
      });
      setMarkerModalSucursal(null);

      if (!isMdUp) {
        setShowModalDialog(true);
      }
    },
    [isMdUp]
  );

  // Efecto para actualizar mapa y título
  useEffect(() => {
    if (!selectedSuc) {
      return;
    }

    setShowModalDialogTitle(selectedSuc.name);
    setSucLatLong(selectedSuc.latLng);
  }, [selectedSuc]);

  // Click en marker del mapa
  const markerClick = useCallback(marker => {
    const latLng = parseLatLng(marker.latitude, marker.longitude);
    if (!latLng) {
      return;
    }

    const key = marker.id ?? marker.name;

    setSelectedSuc({
      key,
      name: marker.name,
      latLng,
    });
    setMarkerModalSucursal(marker);
    setScrollToKey(key);
  }, []);

  const closeMarkerModal = useCallback(() => {
    setMarkerModalSucursal(null);
  }, []);

  const validMarkers = useMemo(
    () =>
      sucursales
        .map(s => {
          const lat = Number(s.latitude);
          const lng = Number(s.longitude);
          if (!isFinite(lat) || !isFinite(lng)) return null;
          return { ...s, latitude: lat, longitude: lng };
        })
        .filter(Boolean),
    [sucursales]
  );

  const sucursalesFiltered = useMemo(() => {
    if (!textFilter) {
      return sucursales;
    }

    return sucursales.filter(suc =>
      suc.name
        .replace(name + '- ', '')
        .toUpperCase()
        .includes(textFilter.toUpperCase())
    );
  }, [sucursales, textFilter, name]);

  return (
    <Grid
      container
      className="minWebPadding"
      sx={{ backgroundColor: '#E5E5E5', py: 2 }}
    >
      <Grid item container direction="column">
        <Grid
          container
          direction="row"
          alignItems="center"
          style={{ marginBottom: 20 }}
        >
          <div
            style={{
              width: 8,
              height: 32,
              backgroundColor: theme.primary,
              marginRight: 5,
            }}
          />
          <Typography
            style={{
              fontSize: 30,
              fontWeight: 'bold',
              color: 'black',
              lineHeight: 1,
            }}
          >
            {'Puntos de Venta'}
          </Typography>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: '#F6F6F6',
            borderRadius: 4,
            p: 2,
            mb: '20px',
          }}
        >
          <StorefrontIcon sx={{ fontSize: 56, color: '#000' }} />
          <Box sx={{ borderLeft: '1px solid #636466', alignSelf: 'stretch' }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#000' }}>
              En Viajes TDH queremos estar cada vez más cerca tuyo.
            </Typography>
            <Typography sx={{ fontSize: 15, color: '#000' }}>
              En esta sección podrás conocer nuestros distintos puntos de venta,
              consultar sus datos de contacto y ubicación en el mapa. Explorá
              las opciones disponibles y encontrá la sucursal de Viajes TDH más
              cercana a vos para que podamos acompañarte en la planificación de
              tu próximo viaje.
            </Typography>
          </Box>
        </Box>

        <Grid item container direction="row">
          <Grid item container xs={12} sm={4}>
            <SubsidiariesList
              sucursalesFiltered={sucursalesFiltered}
              textFilter={textFilter}
              handleTextChange={handleTextChange}
              changeSuc={changeSuc}
              name={name}
              selectedKey={selectedSuc?.key}
              scrollToKey={scrollToKey}
            />
          </Grid>

          <Grid
            item
            container
            xs={12}
            sm={8}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            <Box
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                marginTop: { xs: '35px', md: 0 },
              }}
            >
              <Box
                sx={
                  expandedMap
                    ? {
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1300,
                        backgroundColor: '#fff',
                      }
                    : { position: 'relative', width: '100%', height: '100%' }
                }
              >
                <MapContainer
                  center={sucLatLong}
                  multipleMarkers={validMarkers}
                  customMarker={TDHlogo}
                  zoom={selectedSuc ? 16 : 12}
                  onMarkerClick={markerClick}
                  onMapClick={closeMarkerModal}
                  selectedMarkerId={selectedSuc?.key}
                  expanded={expandedMap}
                  fillParent={!expandedMap}
                >
                  {markerModalSucursal && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: '1.25%',
                        width: { xs: '250px', sm: '55%', md: '40%' },
                        zIndex: 1,
                      }}
                    >
                      <SubsidiaryCard
                        sucursal={markerModalSucursal}
                        name={name}
                        onSelect={() => {}}
                        onClose={closeMarkerModal}
                      />
                    </Box>
                  )}

                  <IconButton
                    onClick={() => setExpandedMap(prev => !prev)}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 1,
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      '&:hover': { backgroundColor: '#F6F6F6' },
                    }}
                  >
                    {expandedMap ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                </MapContainer>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <DialogMapModal
        isOpen={showModalDialog}
        onClose={() => setShowModalDialog(false)}
        title={showModalDialogTitle}
      >
        {showModalDialog && (
          <MapContainer
            center={sucLatLong}
            multipleMarkers={validMarkers}
            customMarker={TDHlogo}
            onMarkerClick={markerClick}
            onMapClick={closeMarkerModal}
            selectedMarkerId={selectedSuc?.key}
          >
            {markerModalSucursal && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: '1.25%',
                  width: { xs: '250px', sm: '55%', md: '40%' },
                  zIndex: 1,
                }}
              >
                <SubsidiaryCard
                  sucursal={markerModalSucursal}
                  name={name}
                  onSelect={() => {}}
                  onClose={closeMarkerModal}
                />
              </Box>
            )}
          </MapContainer>
        )}
      </DialogMapModal>
    </Grid>
  );
};

export default SellPoints;
